import { useMemo, useState } from "react";
import { Check, Copy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface JsonViewerProps {
  /** Any JSON-serializable value. Rendered pretty-printed and syntax-highlighted. */
  value: unknown;
  /** Shown instead of the block when there is nothing to render. */
  emptyLabel?: string;
  /** Tailwind max-height class for the scroll area. */
  maxHeightClassName?: string;
  className?: string;
}

/**
 * Matches the pieces of pretty-printed JSON worth colouring: a string (optionally the key half of
 * a `"key":` pair), a literal, or a number. Everything the split leaves between matches is
 * punctuation and whitespace, which stays in the base colour.
 */
const JSON_TOKEN =
  /("(?:\\.|[^"\\])*"\s*:?|\b(?:true|false|null)\b|-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?)/g;

function tokenClassName(token: string): string {
  if (token.startsWith('"')) {
    return token.trimEnd().endsWith(":")
      ? "text-sky-700 dark:text-sky-300"
      : "text-emerald-700 dark:text-emerald-400";
  }

  if (token === "true" || token === "false" || token === "null") {
    return "text-purple-600 dark:text-purple-300";
  }

  return "text-amber-700 dark:text-amber-400";
}

/**
 * Read-only JSON block with syntax highlighting and a copy button.
 *
 * Built for showing payloads verbatim - provider responses, stored JSON columns - so it neither
 * reorders keys nor drops fields. Highlighting is done by splitting the serialized text on
 * {@link JSON_TOKEN} and wrapping each match in a span, so nothing is ever injected as HTML.
 */
export function JsonViewer({
  value,
  emptyLabel = "No content",
  maxHeightClassName = "max-h-[28rem]",
  className,
}: JsonViewerProps) {
  const [copied, setCopied] = useState(false);

  const text = useMemo(() => {
    if (value === undefined || value === null) return "";
    if (typeof value === "string") return value;
    try {
      return JSON.stringify(value, null, 2);
    } catch {
      // Circular structures cannot happen for parsed JSON, but a caller may pass anything.
      return String(value);
    }
  }, [value]);

  const parts = useMemo(() => (text ? text.split(JSON_TOKEN) : []), [text]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard is unavailable outside a secure context; the text stays selectable.
    }
  };

  if (!text) {
    return (
      <div
        className={cn(
          "rounded-lg border border-border/80 bg-muted/30 px-3 py-6 text-center text-sm text-muted-foreground",
          className,
        )}
      >
        {emptyLabel}
      </div>
    );
  }

  return (
    <div className={cn("relative", className)}>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={handleCopy}
        className="absolute right-2 top-2 z-10 h-7 gap-1.5 px-2 text-xs"
      >
        {copied ? (
          <Check className="h-3.5 w-3.5" />
        ) : (
          <Copy className="h-3.5 w-3.5" />
        )}
        {copied ? "Copied" : "Copy"}
      </Button>
      <pre
        className={cn(
          "overflow-auto rounded-lg border border-border/80 bg-muted/30 p-3 pr-20 font-mono text-xs leading-relaxed text-foreground",
          maxHeightClassName,
        )}
      >
        <code>
          {parts.map((part, index) =>
            index % 2 === 1 ? (
              <span key={index} className={tokenClassName(part)}>
                {part}
              </span>
            ) : (
              part
            ),
          )}
        </code>
      </pre>
    </div>
  );
}
